import { useState } from "react";
import { Check, ChevronDown, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { LocationBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const stop = (e) => e.stopPropagation();

export function LocationPicker({ product, region, onSaved }) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const onOpenChange = async (next) => {
    setOpen(next);
    setAdding(false);
    setNewName("");
    if (next) {
      try {
        const { data } = await api.get("/locations");
        const list = Array.isArray(data) ? data : data.locations || [];
        setLocations([...list].sort((a, b) => (b.region === region) - (a.region === region)));
      } catch (e) {
        setLocations([]);
        toast.error(apiError(e, "Could not load locations"));
      }
    }
  };

  const assign = async (name) => {
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, { buying_location: name });
      toast.success(`Location set to ${name}`);
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Could not save the location"));
    } finally {
      setSaving(false);
    }
  };

  const addAndAssign = async () => {
    const name = newName.trim();
    if (!name) return;
    const existing = (locations || []).find((l) => l.name.trim().toLowerCase() === name.toLowerCase());
    if (existing) {
      await assign(existing.name);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/locations", { name, link: null, region: region || null });
      setSaving(false);
      await assign(data.name);
    } catch (e) {
      toast.error(apiError(e, "Could not add the location"));
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" onClick={stop} data-testid={`location-picker-trigger-${product.id}`} className="inline-flex items-center gap-0.5 rounded-md transition-opacity hover:opacity-80" aria-label="Change buying location">
          <LocationBadge location={product.buying_location} />
          <ChevronDown className="h-3 w-3 text-stone-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" onClick={stop} data-testid="location-picker">
        {adding ? (
          <div className="space-y-2 p-3">
            <label htmlFor="location-picker-new-input" className="text-xs font-semibold text-muted-foreground">New location name</label>
            <Input id="location-picker-new-input" data-testid="location-picker-new-input" className="h-10" autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addAndAssign()} placeholder="e.g. CASH DRUGSTORE" />
            <div className="flex gap-2">
              <Button variant="outline" className="h-10 flex-1" data-testid="location-picker-new-cancel" onClick={() => setAdding(false)}>Back</Button>
              <Button className="h-10 flex-1" data-testid="location-picker-new-save" disabled={saving || !newName.trim()} onClick={addAndAssign}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          <Command>
            <CommandInput placeholder="Search locations…" data-testid="location-picker-search" />
            <CommandList>
              {locations === null ? (
                <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <>
                  <CommandEmpty>No location found</CommandEmpty>
                  <CommandGroup>
                    {locations.map((l) => (
                      <CommandItem key={l.id || l.name} value={l.name} disabled={saving} onSelect={() => l.name !== product.buying_location && assign(l.name)} data-testid={`location-picker-option-${l.name.toLowerCase().replace(/\s+/g, "-")}`}>
                        <Check className={`h-4 w-4 ${l.name === product.buying_location ? "opacity-100" : "opacity-0"}`} />
                        <span className="flex-1 truncate">{l.name}</span>
                        {l.region && <span className="text-[10px] font-semibold uppercase text-muted-foreground">{l.region}</span>}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
            <button type="button" data-testid="location-picker-add-new" onClick={() => setAdding(true)} className="flex w-full items-center gap-2 border-t px-3 py-2.5 text-sm font-semibold text-rose-700 hover:bg-accent">
              <Plus className="h-4 w-4" /> Add New Location
            </button>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}
